import PageContent from "../../layout/PageContent";
import {RangeList} from "../../components/rangelist/RangeList";
import {
   EnumIntegrationMessageMessage,
   FilterFindManyIntegrationMessageInput,
   IntegrationListFragment,
   SortFindManyIntegrationMessageInput,
   useIntegrationConnectorsQuery,
   useIntegrationMessagesManyLazyQuery,
   useRerunMessageMutation,
} from "../../Queries";
import {FlexListColumn} from "../../components/flexlist/FlexTypes";
import dayjs from "dayjs";
import {Box, Button, Chip, Dialog, DialogActions, DialogContent, LinearProgress} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import React, {useEffect, useRef, useState} from "react";
import {extractError, isTerminal, MessageDetailPanel, messageStatus} from "./MessageDetailPanel";
import {RangeCommand, RangeFilter, RangeListResponse, RangeTab} from "../../components/rangelist/RangeListTypes";
import {useRecoilValue} from "recoil";
import {CurrentUserIsAdmin} from "../../atoms/CurrentUser";
import {BodyText} from "../../layout/Typography";
import {scopeMessagesFilter} from "./messagesScopeFilter";

type SelectedRow = {
   refId: string,
   refType: string
   messageId: string
}

const EXCLUDED_INTEGRATION_ID = '64fe30635637b47af6fbb07e';

/** A message the connector has not answered within this window is stuck rather than merely in flight. */
const STUCK_MINUTES = 5;

/** Stand-in for the stuck cut-off, swapped for a fresh bound on every request - see resolveStuckBound. */
const STUCK_BOUND = '__stuckBound__';

/**
 * The server exposes no date operators for created, but ObjectIds are time ordered and _id is indexed,
 * so an id synthesised from a timestamp is an exact "created before" bound.
 */
const objectIdMinutesAgo = (minutes: number): string => {
   const seconds = Math.floor(Date.now() / 1000) - minutes * 60;
   return seconds.toString(16).padStart(8, '0') + '0'.repeat(16);
}

/**
 * Resolves the stuck placeholder at request time. Building it once with the filters would freeze the
 * cut-off at page load, so nothing created afterwards would ever count as stuck.
 */
const resolveStuckBound = (filter: any): any => {
   if (filter === STUCK_BOUND) {
      return objectIdMinutesAgo(STUCK_MINUTES)
   }
   if (Array.isArray(filter)) {
      return filter.map(resolveStuckBound)
   }
   if (filter && typeof filter === 'object') {
      const resolved: any = {};
      Object.keys(filter).forEach(key => {
         resolved[key] = resolveStuckBound(filter[key]);
      });
      return resolved
   }
   return filter
}

type RerunSummary = {
   reran: number
   skipped: number
   failures: string[]
}

type MessagesListProps = {
   /** Namespaces the persisted column and selection state, so each hosting page keeps its own. */
   globalId: string

   /**
    * Restricts the list to these integration instances - both the rows returned and the instances
    * offered in the Integration filter. Left out, every instance is in scope.
    */
   scopeInstanceIds?: string[]
}

export const MessagesList = ({globalId, scopeInstanceIds}: MessagesListProps) => {
   const isAdmin = useRecoilValue(CurrentUserIsAdmin);
   const [selected, setSelected] = useState<SelectedRow | undefined>(undefined);
   const [filters, setFilters] = useState<RangeFilter<FilterFindManyIntegrationMessageInput>[]>([]);
   const [tabs, setTabs] = useState<RangeTab[]>([]);
   const [listVersion, setListVersion] = useState<number>(0);
   const [rerunSummary, setRerunSummary] = useState<RerunSummary | undefined>(undefined);

   // Rows as they are loaded, so a bulk command can tell which of the selected ids may be rerun.
   const loadedRows = useRef<Map<string, IntegrationListFragment>>(new Map());

   const [requestData] = useIntegrationMessagesManyLazyQuery({
      fetchPolicy: "no-cache"
   });
   const [rerunMessage] = useRerunMessageMutation();

   const {data: connectorData} = useIntegrationConnectorsQuery();

   const columns: FlexListColumn<IntegrationListFragment>[] = [{
      id: "time",
      label: "Time",
      get: (row) => {
         return row.created
      },
      render: (data) => {
         const momentInTime = dayjs(data);
         const ageSeconds = Math.abs((new Date().getTime() - momentInTime.toDate().getTime()) / 1000);
         let color: string = "#F8F8F8";
         if (ageSeconds < 60) {
            color = "#00FF00";
         } else if (ageSeconds < 120) {
            color = "#40FF40";
         } else if (ageSeconds < 1200) {
            color = "#80FF80";
         } else if (ageSeconds < 3600) {
            color = "#C0FFC0";
         } else if (ageSeconds < 7200) {
            color = "#F0FFF0";
         }
         return <div style={{display: 'flex'}}>
            <div style={{
               marginRight: '16px',
               border: '1px solid #808080',
               width: '20px',
               backgroundColor: color,
               borderRadius: '50%'
            }}/>
            {momentInTime.format('HH:mm:ss, D. MMM')}
         </div>
      }
   }, {
      id: "connector",
      label: "Connection",
      field: "instanceId",
      render: (_, row) => {
         return row?.instance?.name
      }
   }, {
      id: "name",
      label: "Name",
      get: (row) => {
         return row?.refShop?.name || row?.refBrandHouse?.name || row?.refLogisticsPartner?.name || 'No-name'
      }
   }, {
      id: "message",
      field: "message",
      label: "Message"
   }, {
      id: "Latency",
      label: "Latency",
      field: "_id",
      render: (data, row) => {
         const timing: string[] = []
         if (row?.created) {
            if (row?.sent) {
               const spent = new Date(row.sent).getTime() - new Date(row.created).getTime();
               timing.push(spent + "ms")
            } else {
               timing.push("Not sent")
            }
         } else {
            timing.push("Error")
         }
         return <Box>{timing.join(" / ")}</Box>
      }
   }, {
      id: "Time",
      label: "Response time",
      field: "_id",
      render: (data, row) => {
         const timing: string[] = []
         if (row?.sent) {
            if (row?.resultTs) {
               const spent = new Date(row.resultTs).getTime() - new Date(row.sent).getTime();
               timing.push(spent + "ms")
            } else if (row?.failed) {
               const spent = new Date(row.failed).getTime() - new Date(row.sent).getTime();
               timing.push(spent + "ms")
            }
         } else {
            timing.push("No timing")
         }
         return <Box>{timing.join(" / ")}</Box>
      }
   }, {
      id: "Status",
      label: "Status",
      field: "messageFailed",
      render: (data, row) => {
         if (!row) {
            return null
         }
         const status = messageStatus(row);
         return <Chip color={status.color} label={status.label}/>
      }
   }, {
      id: "error",
      label: "Error",
      field: "result",
      // Column widths are proportional weights - errors need more room than a chip or a timestamp.
      width: 2,
      ellipsis: true,
      render: (data, row) => {
         // The list already fetches result, so triaging a failure costs no extra round trip.
         if (!row?.messageFailed) {
            return null
         }
         const error = extractError(row.result);
         return error ? <Box title={error}>{error}</Box> : null
      }
   }];


   // Data fetcher for RangeList - returns data and totalCount
   const onRequestData = async (search: string | undefined, sort: string | undefined, page: number, perPage: number, filter?: any): Promise<RangeListResponse<IntegrationListFragment>> => {
      const {data} = await requestData({
         variables: {
            page: page,
            sort: SortFindManyIntegrationMessageInput._ID_DESC,
            perPage: perPage,
            filter: scopeMessagesFilter(resolveStuckBound(filter), scopeInstanceIds)
         }
      });
      const items = data?.IntegrationMessages?.items || [];
      items.forEach(item => loadedRows.current.set(item._id, item));
      return {
         data: items,
         totalCount: data?.IntegrationMessages?.count || 0,
      };
   };

   // A stable dependency for the scope - the caller rebuilds the array on every render.
   const scopeKey = scopeInstanceIds?.join(',');

   // Build filters when connector data is loaded
   useEffect(() => {
      if (connectorData) {
         // The query returns every instance; the Integration filter must offer only those in scope.
         const inScope = scopeInstanceIds
            ? connectorData.IntegrationInstances.filter(instance => scopeInstanceIds.includes(String(instance._id)))
            : connectorData.IntegrationInstances;

         const integrationFilter: RangeFilter<FilterFindManyIntegrationMessageInput> = {
            id: 'integration',
            label: 'Integration',
            options: []
         };
         inScope.forEach(instance => {
            integrationFilter.options.push({
               id: instance._id,
               label: instance.connector?.name || 'Unknown connector',
               subtext: instance.refName || undefined,
               filter: {
                  instanceId: instance._id
               }
            });
         });
         integrationFilter.options.sort((a, b) => a.label.localeCompare(b.label));

         setFilters([integrationFilter, {
            id: 'type',
            label: 'Type',
            options: Object.values(EnumIntegrationMessageMessage).map(msg => ({
               label: msg,
               filter: {
                  message: msg
               }
            }))
         }, {
            id: 'status',
            label: 'Status',
            options: [{
               label: 'Failed',
               filter: {
                  messageFailed: true
               }
            }, {
               label: 'Ignored',
               filter: {
                  messageIgnored: true
               }
            }, {
               label: 'Success',
               filter: {
                  messageFailed: false,
               }
            }, {
               id: 'NoResponse',
               label: 'No response',
               filter: {
                  // messageFailed is only written once the connector has given a verdict.
                  _operators: {
                     messageFailed: {
                        exists: false
                     }
                  }
               }
            }]
         }, {
            id: 'age',
            label: 'Age',
            options: [{
               id: 'stuck',
               label: 'Older than ' + STUCK_MINUTES + ' minutes',
               filter: {
                  _operators: {
                     _id: {
                        lt: STUCK_BOUND
                     }
                  }
               }
            }]
         }]);

         // Build tabs with dynamic integration filter (excluding specific instance)
         const allowedIntegrationIds = inScope
            .filter(instance => instance._id !== EXCLUDED_INTEGRATION_ID)
            .map(instance => instance._id);

         setTabs([{
            id: 'all',
            label: 'All',
            filters: []
         }, {
            id: 'failed',
            label: 'Failed',
            showCount: true,
            filters: [{
               filterId: 'status',
               values: ['Failed']
            }]
         }, {
            id: 'fulfill-errors',
            label: 'Fulfill errors',
            showCount: true,
            filters: [{
               filterId: 'status',
               values: ['Failed']
            }, {
               filterId: 'type',
               values: ['FULFILL']
            }, {
               filterId: 'integration',
               values: allowedIntegrationIds
            }]
         }, {
            id: 'refund-errors',
            label: 'Refund errors',
            showCount: true,
            filters: [{
               filterId: 'status',
               values: ['Failed']
            }, {
               filterId: 'type',
               values: ['REFUND']
            }]
         }, {
            // Messages nobody currently sees: sent, never answered, and too old to still be in flight.
            id: 'stuck',
            label: 'Stuck',
            showCount: true,
            filters: [{
               filterId: 'status',
               values: ['NoResponse']
            }, {
               filterId: 'age',
               values: ['stuck']
            }]
         }]);
      }
   }, [connectorData, scopeKey]);

   const onSelect = (row: IntegrationListFragment) => {
      setSelected({
         refId: row.refId,
         refType: row.refType,
         messageId: row._id
      })
   }

   // A rerun creates a new message - remount the list so the new pending message appears. The dialog
   // stays open and follows the new attempt, so the operator can watch it complete.
   const onRerun = () => {
      setListVersion(version => version + 1);
   }

   /**
    * Reruns every selected message that has reached a terminal state. Runs one at a time: each rerun
    * performs the connector action for real, and a burst of them would hit the same integration at once.
    */
   const bulkRerun = async (rowIds: string[]) => {
      const runnable = rowIds.filter(rowId => {
         const row = loadedRows.current.get(rowId);
         // An unknown row is left to the server, which rejects a rerun of an unanswered message anyway.
         return !row || isTerminal(row);
      });
      const failures: string[] = [];
      let reran = 0;
      for (const rowId of runnable) {
         try {
            await rerunMessage({
               variables: {
                  messageId: rowId
               }
            });
            reran++;
         } catch (error) {
            failures.push(rowId.substr(rowId.length - 6) + ': ' + (error instanceof Error ? error.message : String(error)));
         }
      }
      const skipped = rowIds.length - runnable.length;
      if (skipped > 0 || failures.length > 0) {
         setRerunSummary({reran, skipped, failures});
      }
      setListVersion(version => version + 1);
   }

   // No onSelectAll is passed on purpose - "rerun everything matching this filter" is too wide a blast
   // radius for an action that is not exactly-once.
   const commands: RangeCommand<IntegrationListFragment>[] = [{
      id: 'rerun',
      title: 'Rerun',
      icon: ReplayIcon,
      children: [{
         id: 'rerun-confirm',
         title: 'Rerun now - each action may run twice',
         icon: ReplayIcon,
         execute: bulkRerun
      }]
   }];

   // An empty scope means "no messages", which the OR-of-equalities filter cannot express - and
   // querying unscoped here would leak every other owner's messages into the list.
   if (scopeInstanceIds && scopeInstanceIds.length === 0) {
      return <PageContent>
         <BodyText type={"body1"}>No integrations are installed here yet, so there are no events to show.</BodyText>
      </PageContent>
   }

   // Wait for filters and tabs to be loaded before rendering
   if (!connectorData || filters.length === 0 || tabs.length === 0) {
      return <LinearProgress/>
   }

   return <>
      {selected &&
         <Dialog open={!!selected} onClose={() => setSelected(undefined)} fullWidth maxWidth={"lg"}>
            <DialogContent>
               <MessageDetailPanel {...selected} onRerun={onRerun}/>
            </DialogContent>
         </Dialog>
      }
      {rerunSummary &&
         <Dialog open={true} onClose={() => setRerunSummary(undefined)} fullWidth maxWidth={"sm"}>
            <DialogContent>
               <BodyText type={"body1"}>Reran {rerunSummary.reran} message(s)</BodyText>
               {rerunSummary.skipped > 0 &&
                  <BodyText type={"body2"}>
                     Skipped {rerunSummary.skipped} message(s) still awaiting a response
                  </BodyText>
               }
               {rerunSummary.failures.map(failure =>
                  <BodyText key={failure} type={"body2"} sx={{color: 'error.main'}}>{failure}</BodyText>
               )}
            </DialogContent>
            <DialogActions>
               <Button variant={"contained"} onClick={() => setRerunSummary(undefined)}>Close</Button>
            </DialogActions>
         </Dialog>
      }
      <PageContent>
         <RangeList
            key={listVersion}
            globalId={globalId}
            columns={columns}
            itemsPerPage={50}
            onRequestData={onRequestData}
            onSelectRow={onSelect}
            filters={filters}
            tabs={tabs}
            selections={isAdmin}
            commands={isAdmin ? commands : undefined}
         />
      </PageContent>
   </>
}
